/**
 * Toast Schedules View
 * Weekly calendar view of employee shifts from Toast POS
 */
import React, { useState, useMemo } from 'react';

const ToastSchedulesView = ({ shifts, employees, jobs, loading }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');

  // Get employee name by GUID
  const getEmployeeName = (employeeRef) => {
    if (!employeeRef) return 'Unassigned';
    const emp = employees.find(e => e.guid === employeeRef.guid);
    return emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : 'Unknown';
  };

  // Get job name by GUID
  const getJobName = (jobRef) => {
    if (!jobRef) return 'Unknown';
    const job = jobs.find(j => j.guid === jobRef.guid);
    return job?.title || job?.name || 'Unknown';
  };

  // Get week dates based on offset
  const getWeekDates = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + (weekOffset * 7));
    startOfWeek.setHours(0, 0, 0, 0);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Filter and organize shifts by date
  const shiftsByDate = useMemo(() => {
    const organized = {};

    weekDates.forEach(date => {
      const dateKey = date.toISOString().split('T')[0];
      organized[dateKey] = [];
    });

    shifts.forEach(shift => {
      if (!shift.inDate) return;

      // Apply filters
      if (employeeFilter && shift.employeeReference?.guid !== employeeFilter) return;
      if (jobFilter && shift.jobReference?.guid !== jobFilter) return;

      const shiftDate = new Date(shift.inDate);
      const dateKey = shiftDate.toISOString().split('T')[0];

      if (organized[dateKey]) {
        organized[dateKey].push(shift);
      }
    });

    // Sort shifts by time within each day
    Object.keys(organized).forEach(dateKey => {
      organized[dateKey].sort((a, b) => new Date(a.inDate) - new Date(b.inDate));
    });

    return organized;
  }, [shifts, weekDates, employeeFilter, jobFilter]);

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatWeekHeader = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const year = start.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${year}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${year}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const activeEmployees = employees.filter(e => !e.deleted && !e.disabled);

  const getTotalShiftsThisWeek = () => {
    return Object.values(shiftsByDate).reduce((sum, dayShifts) => sum + dayShifts.length, 0);
  };

  return (
    <div className="toast-list-container schedules-view">
      {/* Toolbar */}
      <div className="list-toolbar">
        <div className="week-nav">
          <button className="week-nav-btn" onClick={() => setWeekOffset(w => w - 1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <span className="week-label">{formatWeekHeader()}</span>
          <button className="week-nav-btn" onClick={() => setWeekOffset(w => w + 1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          {weekOffset !== 0 && (
            <button className="today-btn" onClick={() => setWeekOffset(0)}>Today</button>
          )}
        </div>

        <select
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Employees</option>
          {activeEmployees.map(emp => (
            <option key={emp.guid} value={emp.guid}>
              {emp.firstName} {emp.lastName}
            </option>
          ))}
        </select>

        <select
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Positions</option>
          {jobs.map(job => (
            <option key={job.guid} value={job.guid}>
              {job.title || job.name}
            </option>
          ))}
        </select>

        <span className="results-count">
          {getTotalShiftsThisWeek()} shifts this week
        </span>
      </div>

      {/* Calendar Grid */}
      <div className="schedule-calendar">
        <div className="calendar-header">
          {weekDates.map(date => (
            <div key={date.toISOString()} className={`calendar-day-header ${isToday(date) ? 'today' : ''}`}>
              <span className="day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
              <span className="day-number">{date.getDate()}</span>
            </div>
          ))}
        </div>

        <div className="calendar-body">
          {weekDates.map(date => {
            const dateKey = date.toISOString().split('T')[0];
            const dayShifts = shiftsByDate[dateKey] || [];

            return (
              <div key={date.toISOString()} className={`calendar-day ${isToday(date) ? 'today' : ''}`}>
                {dayShifts.length === 0 ? (
                  <div className="no-shifts">No shifts</div>
                ) : (
                  dayShifts.map(shift => (
                    <div key={shift.guid} className="shift-card">
                      <div className="shift-time">
                        {formatTime(shift.inDate)} - {formatTime(shift.outDate)}
                      </div>
                      <div className="shift-employee">{getEmployeeName(shift.employeeReference)}</div>
                      <div className="shift-job">{getJobName(shift.jobReference)}</div>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>

      {shifts.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No schedules found</h3>
          <p>Shift schedules from Toast will appear here once they're created</p>
        </div>
      )}
    </div>
  );
};

export default ToastSchedulesView;
